import React from 'react'
import usePaymentPage from 'src/lib/contexts/PaymentPageContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { PaymentModeUiConfig, PaymentModeUiConfigBE } from 'src/lib/types/paymentPage'
import { classNames } from 'src/lib/utils/helpers'
import { MethodContainer } from './PaymentMethodsPreview'
import { modeLabelKey, modeSubtitleKey } from './configs/subscriptionTileConfig'

const SubscriptionPaymentTilesPreview = () => {
  const { t } = useLocale()

  const {
    getters: { subscriptionConfig, paymentTileConfigs, selection, hoveredMethodId },
    actions: { setSelection }
  } = usePaymentPage()

  const payOnceUpiIntent = paymentTileConfigs.modeDetails.find(entry => entry.mode === 'UPI_INTENT')

  const payOnceTile = (mode: string) => paymentTileConfigs.modeDetails.find(entry => entry.mode === mode)

  const toUiConfig = (tile: PaymentModeUiConfigBE): PaymentModeUiConfig => {
    const payOnce = payOnceTile(tile.mode)
    const labelKey = modeLabelKey(tile.mode)
    const subtitleKey = modeSubtitleKey(tile.mode)
    const label = labelKey ? t(labelKey) : tile.mode

    return {
      mode: tile.mode,
      title: tile.title || payOnce?.title || label,
      modeTitle: payOnce?.modeTitle || label,
      subtitle: tile.subtitle || payOnce?.subtitle || (subtitleKey ? t(subtitleKey) : ''),
      paymentCard: {
        backgroundColor: tile.payment_card.background_color || payOnce?.paymentCard?.backgroundColor || '',
        borderColor: tile.payment_card.border_color || payOnce?.paymentCard?.borderColor || '',
        textColor: tile.payment_card.text_color || payOnce?.paymentCard?.textColor || '',
        iconColor: tile.payment_card.icon_color || payOnce?.paymentCard?.iconColor || '',
        subtitleColor: tile.payment_card.subtitle_color || payOnce?.paymentCard?.subtitleColor || '',
        isSubtitleHidden: tile.payment_card.is_subtitle_hidden
      },
      offerBadge: {
        backgroundColor: tile.offer_badge.background_color || payOnce?.offerBadge?.backgroundColor || '',
        textColor: tile.offer_badge.text_color || payOnce?.offerBadge?.textColor || ''
      },
      isSuggested: tile.is_suggested,
      suggestedTile: {
        backgroundColor: tile.suggested_tile.background_color || payOnce?.suggestedTile?.backgroundColor || '',
        textColor: tile.suggested_tile.text_color || payOnce?.suggestedTile?.textColor || '',
        title: tile.suggested_tile.title || payOnce?.suggestedTile?.title || ''
      },
      icons: tile.icons,
      modeConfig: tile.mode_config ?? payOnceUpiIntent?.modeConfig,
      imgPath: { type: 'Component', component: 'CARD' },
      weight: tile.weight
    }
  }

  const visibleTiles = subscriptionConfig.paymentTiles
    .filter(tile => tile.weight > 0)
    .sort((a, b) => a.weight - b.weight)
    .map(toUiConfig)

  if (!visibleTiles.length) return null

  return (
    <div
      className={classNames(
        selection.type === 'BANNER' || selection.type === 'COUPON' ? 'opacity-50' : '',
        'py-2 gap-2 flex flex-col'
      )}
    >
      <h2 className='text-text-xs-medium text-coal-light px-4'>{t('payments_tab.payment_methods')}</h2>

      <div className='px-4 flex flex-col' style={{ gap: paymentTileConfigs.layoutUi.paymentTileGap }}>
        {visibleTiles.map(tile => (
          <MethodContainer
            key={tile.mode}
            mode={tile}
            isSelected={selection.type === 'METHOD' && selection.id === tile.mode}
            isHovered={hoveredMethodId === tile.mode}
            onSelect={() => setSelection({ type: 'METHOD', id: tile.mode })}
            hasAnySelected={selection.type !== null && selection.id !== null}
            bannerSelected={selection.type === 'BANNER' || selection.type === 'COUPON'}
            hideOfferBadge
          />
        ))}
      </div>
    </div>
  )
}

export default SubscriptionPaymentTilesPreview
